import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Bell, X, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export function NotificationBell({ dark = true }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/notifications").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);
  const unread = items.filter(n => !n.read).length;
  const markAll = async () => { await api.post("/notifications/mark-all-read"); load(); };
  const markOne = async (id) => { await api.post(`/notifications/${id}/read`); load(); };
  const iconColor = dark ? "text-white/80 hover:text-white" : "text-slate-500 hover:text-[#003DA5]";
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className={`relative p-2 rounded-lg ${iconColor}`} data-testid="notif-bell">
        <Bell className="w-5 h-5" />
        {unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-[#FFB547] text-[#5C3E00] text-[10px] font-bold flex items-center justify-center">{unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-xl shadow-2xl border border-slate-200 z-50" data-testid="notif-panel">
          <div className="flex items-center justify-between p-3 border-b sticky top-0 bg-white">
            <div className="font-heading font-semibold text-[#001F54]">Notifications</div>
            <div className="flex gap-2">
              {unread > 0 && <button onClick={markAll} className="text-xs text-[#003DA5] font-semibold hover:underline" data-testid="notif-mark-all">Mark all read</button>}
              <button onClick={() => setOpen(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </div>
          </div>
          {items.length === 0 ? <div className="p-6 text-sm text-slate-500 text-center">No notifications yet.</div> : (
            <ul>
              {items.map(n => (
                <li key={n.id} className={`p-3 border-b border-slate-100 flex items-start gap-2 ${!n.read ? "bg-[#F3F7FC]" : ""}`} data-testid={`notif-${n.id}`}>
                  <div className={`w-2 h-2 mt-1.5 rounded-full ${n.level === "success" ? "bg-emerald-500" : "bg-[#003DA5]"}`} />
                  <div className="flex-1">
                    <div className="text-sm">{n.message}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{timeAgo(n.created_at)}</div>
                  </div>
                  {n.target_id && <Link to="/targets" onClick={() => { setOpen(false); markOne(n.id); }} className="text-xs text-[#003DA5] font-semibold hover:underline">View</Link>}
                  {!n.read && <button onClick={() => markOne(n.id)} className="text-slate-400 hover:text-emerald-600" title="Mark read"><CheckCircle2 className="w-4 h-4" /></button>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
